import { type ContextProviderComponent, createContext, createEffect, on, Show } from "solid-js";
import { initializedContext } from "../utils/initializedUtil";
import UserService from "../services/UserService";
import type { LanguageCode } from "./i18n";
import { useTranslation } from "./i18n";
import type GroupId from "./GroupId";
import type UserId from "./UserId";
import type { Grant } from "./grant";
import { isNil } from "lodash-es";

export type User = {
  id: UserId;
  username: string;
  emailAddress: string;
  firstName?: string;
  lastName?: string;
  language: LanguageCode;
  timezone: string;
  emailValidated?: boolean;
  grants: Grant[];
  groups: GroupId[];
  externallyManaged: boolean;
  disabled: boolean;
  phoneNumber?: string;
  receiveNewsLetter: boolean;
  finishedTour: boolean;
};

type State = () => User;
const StateContext = createContext<State>();

export const UserProvider: ContextProviderComponent<undefined> = (props) => {
  const { changeLanguage } = useTranslation();
  const userQuery = UserService.getUser();
  const permissionsQuery = UserService.getPermissions();

  createEffect(
    on(
      () => userQuery.data,
      (data, prevData) => {
        if (!isNil(data) && data.language !== prevData?.language) {
          // Change to language stored on server
          void changeLanguage(data.language);
        }
      },
    ),
  );

  return (
    // Ensure both the user and it's permissions are loaded, to avoid UI flickering
    <Show when={userQuery.isSuccess && permissionsQuery.isSuccess}>
      <Show when={userQuery.data}>
        {(user) => <StateContext.Provider value={user}>{props.children}</StateContext.Provider>}
      </Show>
    </Show>
  );
};

export const useUserState = () => initializedContext(StateContext, "useUserState", "UserProvider");
